import React from 'react';
import { Check, Eye, Sparkles, Briefcase, Zap, Package } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { CollectionOption } from '../types';

// Map collection IDs to icons
const COLLECTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  fashion: Sparkles,
  business: Briefcase,
  sport: Zap,
};

interface CollectionCardProps {
  collection: CollectionOption;
  selected: boolean;
  onSelect: () => void;
  onShowDetails: () => void;
}

/**
 * Card for one collection option: image, title, short description and the
 * select/details actions. The selected card carries a primary ring.
 */
export function CollectionCard({
  collection,
  selected,
  onSelect,
  onShowDetails,
}: CollectionCardProps) {
  // Get icon for collection (default to Package if not found)
  const IconComponent = COLLECTION_ICONS[collection.id] || Package;

  return (
    <Card
      padding="none"
      className={cn(
        'flex flex-col overflow-hidden transition-[box-shadow] duration-[120ms]',
        selected && 'ring-2 ring-primary'
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={`Select ${collection.title}`}
        className="relative block h-32 w-full overflow-hidden bg-paper-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      >
        <img
          src={collection.image}
          alt=""
          className="h-full w-full object-cover"
        />
        {selected && (
          <span
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-pill bg-primary text-primary-fg"
            aria-hidden="true"
          >
            <Check className="h-4 w-4" />
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-primary-soft text-primary"
            aria-hidden="true"
          >
            <IconComponent className="h-4 w-4" />
          </span>
          <span className="font-display font-semibold text-ink">
            {collection.title}
          </span>
        </div>
        <p className="line-clamp-2 text-xs text-ink-2">
          {collection.description}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={onShowDetails}>
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            Details
          </Button>
          <Button
            variant={selected ? 'primary' : 'outline'}
            size="sm"
            onClick={onSelect}
            aria-pressed={selected}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
